import { z } from 'zod';
import operatorApiFetch from './apiService';
import { apiSuccess, apiList, parseApiResponse } from '@services/apiSchemas';

// 對應 app/controllers/api/operator/v1/return_receipts_controller.rb
//
// index (scope=pending_convert) → render_data_list with return_receipt_list_payload
// init_draft / verify / save_progress / reopen → render_data_item with return_receipt_detail_payload
// convert_to_inbound → render_data_item({ inbound_id, inbound_name })
//
// Callers (PendingConvert.jsx / ReturnOrderVerification/Index.tsx) check `data.success`
// 然後 branch 處理 message / interpolations，所以這支 service 一律走 parseApiResponse
// 讓 422 / 5xx 的 success:false envelope 不會被 zod throw 掉（caller 才能拿到 errorCode）。

const ProductForVerificationSchema = z.object({
  id: z.string(),
  sku: z.string().nullable(),
  name: z.string().nullable(),
  barcode: z.string().nullable(),
  validity_days: z.number().nullable(),
  batch_management_required: z.boolean(),
  manufacturing_date_required: z.boolean(),
  required_serial_numbers_count: z.number().nullable(),
  serial_number_pattern: z.string().nullable(),
  box_serial_number_delimiter: z.string().nullable(),
  small_quantity_per_large_unit: z.number().nullable(),
}).passthrough();

const InventoryTypeRefSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  code: z.string().nullable(),
}).passthrough();

const ReturnOrderRefSchema = z.object({
  id: z.string(),
  name: z.string(),
  status: z.string(),
}).passthrough();

const MerchantRefSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
}).passthrough();

// return_receipt_list_payload — 列表 row（含 items 摘要）
const ReturnReceiptListItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  merchant_id: z.string().nullable(),
  merchant_name: z.string().nullable(),
  return_order_id: z.string().nullable(),
  return_order_name: z.string().nullable(),
  fulfillment_id: z.string().nullable(),
  fulfillment_number: z.string().nullable(),
  fulfillment_name: z.string().nullable(),
  status: z.string(),
  tracking_number: z.string().nullable(),
  verifier_name: z.string().nullable(),
  verified_at: z.string().nullable(),
  inbound_id: z.string().nullable(),
  note: z.string().nullable(),
  items: z.array(z.object({
    id: z.string(),
    product_id: z.string().nullable(),
    product_sku: z.string().nullable(),
    product_name: z.string().nullable(),
    inventory_type_name: z.string().nullable(),
    batch: z.string().nullable(),
    expiration_date: z.string().nullable(),
    quantity: z.number().nullable(),
    received_quantity: z.number().nullable(),
  }).passthrough()),
  created_at: z.string(),
  updated_at: z.string(),
}).passthrough();

const SerialNumberRowSchema = z.object({
  id: z.string(),
  number: z.string(),
  product_serial_number_id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  return_receipt_item_id: z.string(),
  created_at: z.string(),
}).passthrough();

export type SerialNumberRow = z.infer<typeof SerialNumberRowSchema>;

// return_receipt_detail_payload 內的 item shape
const ReturnReceiptDetailItemSchema = z.object({
  id: z.string(),
  product_id: z.string().nullable(),
  product: ProductForVerificationSchema.nullable(),
  inventory_type_id: z.string().nullable(),
  inventory_type: InventoryTypeRefSchema,
  return_order_item_id: z.string().nullable(),
  unplanned: z.boolean(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  batch: z.string().nullable(),
  quantity: z.number().nullable(),
  received_quantity: z.number().nullable(),
  custom_attributes: z.unknown(),
  serial_numbers: z.array(z.string()),
  note: z.string().nullable(),
}).passthrough();

// return_receipt_detail_payload — init_draft / verify / save_progress / reopen 的 data 形狀
const ReturnReceiptDetailSchema = z.object({
  id: z.string(),
  name: z.string(),
  merchant_id: z.string().nullable(),
  merchant: MerchantRefSchema,
  return_order_id: z.string().nullable(),
  return_order: ReturnOrderRefSchema.nullable(),
  status: z.string(),
  tracking_number: z.string().nullable(),
  box_count: z.number().nullable(),
  shipping_fee: z.number().nullable(),
  note: z.string().nullable(),
  verified_at: z.string().nullable(),
  verifier_name: z.string().nullable(),
  done_at: z.string().nullable(),
  inbound_id: z.string().nullable(),
  inbound_name: z.string().nullable(),
  items: z.array(ReturnReceiptDetailItemSchema),
  created_at: z.string(),
  updated_at: z.string(),
  verifiable: z.boolean(),
  completable: z.boolean(),
  cancelable: z.boolean(),
  reopenable: z.boolean(),
}).passthrough();

// convert_to_inbound 成功 payload — render_data_item({ inbound_id, inbound_name })
const ConvertToInboundResultSchema = z.object({
  inbound_id: z.string(),
  inbound_name: z.string(),
}).passthrough();

export type ReturnReceiptListItem = z.infer<typeof ReturnReceiptListItemSchema>;
export type ReturnReceiptDetail = z.infer<typeof ReturnReceiptDetailSchema>;
export type ConvertToInboundResult = z.infer<typeof ConvertToInboundResultSchema>;

// Receipt-items payload — 對應 controller 的 return_receipt_items_attributes
// （:product_id, :inventory_type_id, :expiration_date, :manufacturing_date, :batch,
// :quantity, :received_quantity, :note, :return_order_item_id, :_destroy,
// :custom_attributes, :serial_numbers）。verify / save_progress 兩者都吃這個 shape。
// 用 Record<string, unknown> 而非精準 interface — caller 端各自組 items 時欄位差異大。
type ReturnReceiptItemAttributes = Record<string, unknown>;

interface ConvertToInboundParams {
  return_receipt_ids: string[];
  note?: string | null;
}

interface InitDraftParams {
  returnOrderId?: string;
  resumeReceiptId?: string | null;
}

const returnReceiptService = {
  // 待轉換列表（Pending convert list）
  getPendingConvert: async (params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch('return_receipts', {
      method: 'GET',
      params: { scope: 'pending_convert', ...params },
    });
    return parseApiResponse(res, apiList(ReturnReceiptListItemSchema));
  },

  // 轉換為入庫（Convert to inbound）
  convertToInbound: async ({ return_receipt_ids, note }: ConvertToInboundParams) => {
    const res = await operatorApiFetch('return_receipts/convert_to_inbound', {
      method: 'POST',
      body: {
        return_receipt_ids,
        note,
      },
    });
    return parseApiResponse(res, apiSuccess(ConvertToInboundResultSchema));
  },

  // 撤回驗收（verified → pending；已轉入庫則不可撤回）
  reopen: async (id: string) => {
    const res = await operatorApiFetch(`return_receipts/${id}/reopen`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(ReturnReceiptDetailSchema));
  },

  // 作廢驗收單（pending/in_progress/verified → canceled；已轉入庫則不可作廢）
  cancel: async (id: string) => {
    const res = await operatorApiFetch(`return_receipts/${id}/cancel`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(ReturnReceiptDetailSchema));
  },

  // 初始化草稿（init draft）— 支援可選的 resumeReceiptId 以續傳已存在的驗收單。
  // Merchant 由 BE 從 return_order.merchant 推導，FE 不再傳 merchant_id。
  initDraft: async ({ returnOrderId, resumeReceiptId }: InitDraftParams = {}) => {
    const res = await operatorApiFetch('return_receipts/init_draft', {
      method: 'POST',
      body: {
        return_order_id: returnOrderId,
        resume_receipt_id: resumeReceiptId,
      },
    });
    return parseApiResponse(res, apiSuccess(ReturnReceiptDetailSchema));
  },

  // Verify a pending receipt — sends items_attributes and triggers AASM verify!
  verify: async (id: string, items: ReturnReceiptItemAttributes[]) => {
    const res = await operatorApiFetch(`return_receipts/${id}/verify`, {
      method: 'POST',
      body: {
        return_receipt: {
          return_receipt_items_attributes: items,
        },
      },
    });
    return parseApiResponse(res, apiSuccess(ReturnReceiptDetailSchema));
  },

  // 暫存進度：寫 items 並把 receipt 推進 :in_progress（未驗收完成的中間狀態）。
  // 對應「離開頁面 → 暫存後離開」分支；下次掃同一張 RO 走 resume 路徑會把這批
  // items 蓋回畫面繼續驗收。後端：POST /:id/save_progress（見
  // app/controllers/api/operator/v1/return_receipts_controller.rb#save_progress）。
  saveProgress: async (id: string, items: ReturnReceiptItemAttributes[]) => {
    const res = await operatorApiFetch(`return_receipts/${id}/save_progress`, {
      method: 'POST',
      body: {
        return_receipt: {
          return_receipt_items_attributes: items,
        },
      },
    });
    return parseApiResponse(res, apiSuccess(ReturnReceiptDetailSchema));
  },

  // 取得單張 RR 的序號清單（TableIndex 的 getRecords 介面）
  getSerialNumbers: async (id: string, params: Record<string, unknown> = {}) => {
    const res = await operatorApiFetch(`return_receipts/${id}/serial_numbers_get_records`, {
      method: 'POST',
      body: params,
    });
    return parseApiResponse(res, apiList(SerialNumberRowSchema));
  },
};

export { returnReceiptService };
export default returnReceiptService;
